import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MetricGroupCard from "./MetricGroupCard";
import { default as axios } from "../axiosConfig";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { API_GetContractorUserSummary, API_GetExpiringJobContracts } from "../Assets/ApiUrlContainer";
import { ModalPrompt } from "./ModalPrompt";

export default function ContractorLandingPage({ user }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [modalToggleState, setModalToggleState] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const navigate = useNavigate();
  const isLoading = !summary && !error;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API_GetContractorUserSummary}?email=${user.Email}`);
        setSummary(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching contractor landing summary:", err);
        setError("Failed to load dashboard data. Please try refreshing the page.");
      }
    };

    if (user?.Email) {
      fetchSummary();
    }
  }, [user?.Email]);

  useEffect(() => {
    const fetchExpiringContracts = async () => {
      try {
        const response = await axios.get(`${API_GetExpiringJobContracts}/${user.Id}/${user.IsGoogleUser}`);
        setExpiringContracts(response.data || []);

      } catch (err) {
        console.log("Error fetching the expiring contracts: ", err);
      }
    }

    fetchExpiringContracts();
  }, [user])

  const openExpiringContractsModal = async () => {
    setModalToggleState(!modalToggleState);
  }

  return (
    <>
      <div className="landing-content">
        <div className="metric-panel">
          <div className="welcome-header">
            <h1>
              Welcome {user.FirstName} {user.LastName}
            </h1>
          </div>

          {error && <div className="error-message">{error}</div>}

          {!isLoading && summary && (
            <>
              <div className="grid-two-columns">
                {/* LEFT COLUMN */}
                <div className="multGroupedCards">
                  <MetricGroupCard
                    className="group-card group-user"
                    title={
                      <>
                        <GroupIcon style={{ marginRight: "8px" }} />
                        Timesheets
                      </>
                    }
                    metrics={[
                      {
                        label: "To Submit",
                        value: summary.timesheets?.toSubmit ?? 0,
                        onClick: () => navigate("/tasks/timesheets"),
                      },
                      {
                        label: "Rejected",
                        value: summary.timesheets?.rejected ?? 0,
                        onClick: () => navigate("/tasks/timesheets"),
                      },
                    ]}
                  />

                  <MetricGroupCard
                    className="group-card group-jobs"
                    title={
                      <>
                        <WorkOutlineIcon style={{ marginRight: "8px" }} />
                        Forms
                      </>
                    }
                    metrics={[
                      {
                        label: "To Submit",
                        value: summary.forms?.toSubmit ?? 0,
                        onClick: () => navigate("/forms"),
                      },
                      {
                        label: "To Re-upload",
                        value: summary.forms?.toReupload ?? 0,
                        onClick: () => navigate("/forms"),
                      },
                    ]}
                  />
                </div>

                {/* RIGHT COLUMN */}
                <div className="multGroupedCards">
                  <MetricGroupCard
                    className="group-card group-user"
                    title={
                      <>
                        <AssignmentTurnedInIcon style={{ marginRight: "8px" }} />
                        Leave Requests
                      </>
                    }
                    metrics={[
                      {
                        label: "Pending",
                        value: summary.leaves?.pending ?? 0,
                        onClick: () => navigate("/tasks/leaverequests"),
                      },
                      {
                        label: "Rejected",
                        value: summary.leaves?.rejected ?? 0,
                        onClick: () => navigate("/tasks/leaverequests"),
                      },
                    ]}
                  />

                  <MetricGroupCard
                    className="group-card group-jobs"
                    title={
                      <>
                        <WorkOutlineIcon style={{ marginRight: "8px" }} />
                        Job Activity
                      </>
                    }
                    metrics={[
                      {
                        label: "New Jobs This Week",
                        value: summary.jobs?.newJobsThisWeek ?? 0,
                        onClick: () => navigate("/jobs"),
                      },
                      {
                        label: "Applications This Week",
                        value: summary.jobs?.applicationsThisWeek ?? 0,
                      },
                    ]}
                  />
                </div>
              </div>

              {/* BOTTOM – CONTRACTS */}
              <MetricGroupCard
                className="group-card group-contracts"
                title={
                  <>
                    <CalendarMonthIcon style={{ marginRight: "8px" }} />
                    Contracts
                  </>
                }
                metrics={[
                  {
                    label: "Expiring Soon",
                    value: summary.contracts?.expiringSoon ?? 0,
                    onClick: () => openExpiringContractsModal()
                  },
                ]}
              />
            </>
          )}
        </div>
      </div>

      <ModalPrompt
        onModalClose={() => setModalToggleState(!modalToggleState)}
        customModalContainerStyles={expiringContracts.length > 0 && { width: '50% !important' }}
        modalToggleState={modalToggleState}
        modalTitle={"Expiring Job Contacts"}
        modalContent={
          <>
            <div className="expiringContractsModal">
              {expiringContracts.length === 0 && (
                <div className="noContracts">
                  <span>There are no contracts expiring within 30 days</span>
                </div>
              )}

              {expiringContracts.length > 0 && (
                <>
                  <div className="contractEntryHeading">
                    <div className="jobName">Job</div>
                    <div className="candidateName">Candidate</div>
                    <div className="expiration">Expiration Date</div>
                  </div>

                  <div className="contractEntryDataContainer">
                    {expiringContracts.map(contract => (
                      <div className="contractEntryData" key={contract.ContractId}>
                        <div className="jobName"><div className="spanText">{contract.JobName}</div></div>
                        <div className="candidateName"><div className="spanText">{contract.CandidateName}</div></div>
                        <div className="expiration"><div className="spanText">{contract.ContractExpireDate.split("T")[0]}</div></div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>
          </>
        }
      />
    </>
  );
}